import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Message {
  type: 'success' | 'error' | 'info' | 'warning';
  text: string;
}

@Injectable({
  providedIn: 'root'
})
export class MessageService {
  private successMessageSubject = new BehaviorSubject<string>('');
  private errorMessageSubject = new BehaviorSubject<string>('');
  private warningMessageSubject = new BehaviorSubject<string>('');
  private loadingSubject = new BehaviorSubject<boolean>(false);

  public successMessage$ = this.successMessageSubject.asObservable();
  public errorMessage$ = this.errorMessageSubject.asObservable();
  public warningMessage$ = this.warningMessageSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  constructor() {}

  showSuccess(message: string, duration: number = 3000): void {
    this.successMessageSubject.next(message);
    setTimeout(() => {
      this.successMessageSubject.next('');
    }, duration);
  }

  showError(message: string, duration: number = 5000): void {
    this.errorMessageSubject.next(message);
    setTimeout(() => {
      this.errorMessageSubject.next('');
    }, duration);
  }

  showWarning(message: string, duration: number = 4000): void {
    this.warningMessageSubject.next(message);
    setTimeout(() => {
      this.warningMessageSubject.next('');
    }, duration);
  }

  setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  /**
   * Formata uma mensagem de erro incluindo o código se disponível
   * @param error Erro HTTP
   * @param mensagemPadrao Mensagem padrão caso não haja mensagem no erro
   * @returns Mensagem formatada com código se disponível
   */
  formatErrorMessage(error: any, mensagemPadrao: string = 'Erro inesperado'): string {
    const codigoErro = error.error?.codigoErro;
    const mensagem = error.error?.message || mensagemPadrao;
    return this.appendCodigoErro(mensagem, codigoErro);
  }

  /**
   * Trata erros HTTP e exibe mensagem apropriada
   * @param error Erro HTTP
   * @param contexto Contexto da operação (ex: "buscar os registros de histórico", "adicionar um perfil")
   */
  handleError(error: any, contexto?: string): void {
    console.error('Erro capturado:', error);

    const codigoErro = error.error?.codigoErro;
    const errorMessage = this.getErrorMessageByStatus(error, contexto, codigoErro);

    this.showError(errorMessage, codigoErro ? 8000 : 5000);
  }

  private getErrorMessageByStatus(error: any, contexto?: string, codigoErro?: string): string {
    const statusHandlers: Record<number, () => string> = {
      0: () => this.appendCodigoErro('Não foi possível conectar ao servidor. Verifique sua conexão.', codigoErro),
      400: () => {
        const msg = typeof error.error === 'string'
          ? error.error
          : (error.error?.message || 'Dados inválidos. Verifique as informações.');
        return this.appendCodigoErro(msg, codigoErro);
      },
      401: () => error.error?.message || 'Acesso negado. Faça login novamente.',
      403: () => 'Você não tem permissão para realizar esta ação.',
      404: () => 'Recurso não encontrado.',
      500: () => this.getError500Message(contexto, codigoErro)
    };

    const handler = statusHandlers[error.status];
    if (handler) {
      return handler();
    }

    if (error.error?.message) {
      return this.appendCodigoErro(error.error.message, codigoErro);
    }

    return 'Ocorreu um erro inesperado. Tente novamente.';
  }

  private getError500Message(contexto?: string, codigoErro?: string): string {
    if (contexto && codigoErro) {
      return `Ocorreu um erro ao ${contexto} (Código: ${codigoErro})`;
    }
    if (codigoErro) {
      return `Erro interno do servidor (Código: ${codigoErro})`;
    }
    if (contexto) {
      return `Ocorreu um erro ao ${contexto}. Entre em contato com o suporte.`;
    }
    return 'Erro interno do servidor. Entre em contato com o suporte.';
  }

  private appendCodigoErro(mensagem: string, codigoErro?: string): string {
    return codigoErro ? `${mensagem} (Código: ${codigoErro})` : mensagem;
  }

  clear(): void {
    this.successMessageSubject.next('');
    this.errorMessageSubject.next('');
    this.warningMessageSubject.next('');
    this.loadingSubject.next(false);
  }
}
